import { useCallback, useMemo, useRef, useState } from 'react';

export type Currency = 'USD' | 'EUR' | 'GBP' | 'BRL';

type Rates = Record<string, number>;

export function useFxRates() {
  const cacheRef = useRef<Record<string, { rates: Rates; timestamp: number }>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache duration: 1 hour (3600000 ms)
  const CACHE_DURATION = 3600000;

  const fetchRates = useCallback(async (base: Currency): Promise<Rates> => {
    const now = Date.now();
    const cached = cacheRef.current[base];
    
    // Return cached rates if they're still fresh
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      return cached.rates;
    }

    setLoading(true);
    setError(null);
    try {
      // Use the open.er-api.com endpoint as specified
      const res = await fetch(`https://open.er-api.com/v6/latest/${base}`);
      if (!res.ok) throw new Error(`FX fetch failed: ${res.statusText}`);
      const data = await res.json();
      
      if (data.result !== 'success' || !data.rates) {
        throw new Error('Invalid API response format');
      }
      
      const rates: Rates = data.rates;
      // Cache the rates with timestamp
      cacheRef.current[base] = { rates, timestamp: now };
      return rates;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'FX fetch failed';
      setError(msg);
      // Return cached rates if available, even if stale
      return cached?.rates || {};
    } finally {
      setLoading(false);
    }
  }, []);

  const convert = useCallback(async (
    amount: number,
    from: Currency,
    to: Currency
  ): Promise<number> => {
    if (!amount || from === to) return amount;
    const rates = await fetchRates(from);
    const rate = rates[to];
    if (!rate) return amount; // fallback: no conversion
    return amount * rate;
  }, [fetchRates]);

  const roundToNearestFive = useCallback((amount: number): number => {
    return Math.ceil(amount / 5) * 5;
  }, []);

  const getCachedOrSame = useCallback((amount: number, from: Currency, to: Currency): number => {
    if (!amount || from === to) return amount;
    const cached = cacheRef.current[from];
    const rate = cached?.rates?.[to];
    return rate ? amount * rate : amount;
  }, []);

  return useMemo(() => ({ convert, getCachedOrSame, roundToNearestFive, loading, error }), [convert, getCachedOrSame, roundToNearestFive, loading, error]);
}


